import {
  IProduct,
  ProductID,
  type ProductRecord,
} from '../models/product.model';
import {
  Baseline,
  IElement,
  IProductElement,
  IProductReference,
  OneOfElements,
  PercentileFactorRange,
  ProductElementOverride,
  ProductElementOverrides,
  ProductElementOverrideScope,
  ProductElementOverrideTarget,
  ProductMarketEstimate,
  Project,
  ProjectMarketEstimates,
} from '../models/project.model';
import type { Recipe } from '../models/recipe.model';
import {
  getProductEstimates,
  percentileToFactor,
} from './product-market-estimates.helpers';
import { validateGenericProductId } from '../validation/product-validation';
import { isOneOf, shallowEqualMap } from './array.helpers';
import { ConcreteProducts } from '../templates/categories/concrete/concrete.model';
import {
  getEpdProduct as getEPDProduct,
  getProductById,
  ProductRecordOwners,
} from './product.helpers';
import {
  ConversionFactorUnit,
  ConversionFactors,
  Results,
} from '../models/unit.model';
import {
  getA5FromWasteFactorPercentage,
  getCombinedConversionFactors,
  getProductWasteFactor,
  kmToA4,
} from './results.helpers';
import { getConversionFactorValue } from './conversion-factors.helpers';
import { genericProductsLookup } from '../generic-products';
import {
  getAllBuildingVersions,
  getAllProductElements,
  getChildElements,
  isBuildingVersionElement,
  isElement,
  isProductElement,
  type OneOfSearchableElements,
} from './recursive-element.helpers';
import { guards } from './typeguards.helpers';
import { cloneDeep, isEqual } from 'lodash';
import { assertUnreachable } from './function.helpers';
import {
  type ProjectChange,
  ProjectChangeType,
} from './pipeline/update-engine.models';

export const hasProductElementEpd = ({
  product_id,
  generic_product_id,
}: IProductReference): boolean =>
  !!generic_product_id && !!product_id && generic_product_id !== product_id;

/**
 * Clear the EPD from a product element.
 * @param productElement
 * @returns
 */
export const clearProductElementEpd = (
  productElement: IProductElement,
): IProductElement => {
  // Only clear if the product element has an EPD
  if (getEPDProductId(productElement)) {
    // Use generic product id as product_id
    const product_id = getGenericProductId(productElement);
    return {
      ...productElement,
      product_id,
      generic_product_id: undefined,
    };
  }
  return productElement;
};

/**
 * Bundled generic ids plus persisted (non-deleted) catalog ids.
 * Deleted custom products are omitted so {@link remapDeletedProductIdInPlace} can fall back
 * to {@link IProductElement.generic_product_id}.
 *
 * Project trees should pass the result through {@link liveProductIdsWithVersionSnapshot}
 * so copies still stored on {@link Baseline.products} are treated as live.
 */
export const createLiveProductIdSet = (
  persistedLiveProductIds: Iterable<ProductID>,
): Set<ProductID> =>
  new Set([...Object.keys(genericProductsLookup), ...persistedLiveProductIds]);

/**
 * Catalog live ids plus product snapshots on {@link Baseline.products}.
 * A custom/EPD product deleted from the catalog stays valid for a version that
 * still holds a copy.
 */
export const liveProductIdsWithVersionSnapshot = (
  catalogLiveIds: ReadonlySet<ProductID>,
  version: Pick<Baseline, 'products'>,
): ReadonlySet<ProductID> => {
  const snapshotIds = Object.keys(version.products ?? {});
  if (snapshotIds.length === 0) return catalogLiveIds;
  const live = new Set(catalogLiveIds);
  for (const productId of snapshotIds) {
    live.add(productId);
  }
  return live;
};

/** Deleted catalog id → live generic used as fallback. */
type RemappedProductIdMap = Map<ProductID, ProductID>;

/**
 * If {@link IProductElement.product_id} is not in {@link liveProductIds} and a generic
 * fallback exists, point `product_id` at that generic (same as {@link clearProductElementEpd}).
 * Mutates {@link element}. Returns whether it changed.
 */
export const remapDeletedProductIdInPlace = (
  element: IProductElement,
  liveProductIds: ReadonlySet<ProductID>,
  remappedIds?: RemappedProductIdMap,
): boolean => {
  if (liveProductIds.has(element.product_id)) return false;
  const genericId = element.generic_product_id;
  if (!genericId || genericId === element.product_id) return false;
  if (!liveProductIds.has(genericId)) return false;
  remappedIds?.set(element.product_id, genericId);
  element.product_id = genericId;
  element.generic_product_id = undefined;
  return true;
};

/**
 * Remap deleted `product_id` refs on every product element under {@link root}.
 * Mutates the tree. Returns whether anything changed.
 */
export const remapDeletedProductIdsInTree = (
  root: OneOfSearchableElements,
  liveProductIds: ReadonlySet<ProductID>,
  remappedIds?: RemappedProductIdMap,
): boolean => {
  let changed = false;
  for (const element of getAllProductElements(root)) {
    if (remapDeletedProductIdInPlace(element, liveProductIds, remappedIds)) {
      changed = true;
    }
  }
  return changed;
};

/**
 * Remap deleted `product_id` refs in a catalog {@link Recipe} document.
 */
export const remapDeletedProductIdsInRecipe = (
  recipe: Recipe,
  liveProductIds: ReadonlySet<ProductID>,
): boolean => remapDeletedProductIdsInTree(recipe, liveProductIds);

/**
 * Live {@link genericProductId} to replace a deleted {@link productId}, or `undefined`
 * when the stored id is already live or has no live fallback.
 */
const getLiveFallbackProductId = (
  productId: ProductID,
  genericProductId: ProductID | undefined,
  liveProductIds: ReadonlySet<ProductID>,
): ProductID | undefined => {
  if (liveProductIds.has(productId)) return undefined;
  if (!genericProductId || genericProductId === productId) return undefined;
  if (!liveProductIds.has(genericProductId)) return undefined;
  return genericProductId;
};

/**
 * Remap deleted `product_id` refs on a factory or element root, including nested
 * product children on system-element factories ({@link ProjectChangeType.ElementAdd}).
 */
const remapDeletedProductIdsInFactory = (
  root: unknown,
  liveProductIds: ReadonlySet<ProductID>,
  remappedIds: RemappedProductIdMap,
): boolean => {
  if (isBuildingVersionElement(root)) {
    return remapDeletedProductIdsInTree(
      root,
      liveProductIdsWithVersionSnapshot(liveProductIds, root),
      remappedIds,
    );
  }
  if (isProductElement(root) || isElement(root)) {
    return remapDeletedProductIdsInTree(root, liveProductIds, remappedIds);
  }
  return false;
};

/**
 * Remap deleted product ids on an action payload that still names them
 * ({@link ProjectChangeType.ElementRecipeApply} subtrees, EPD swaps, add-factory,
 * product-id switches, category-enrich children, version-add factories).
 *
 * {@link liveProductIds} is catalog + the version that owns the action.
 * {@link catalogLiveIds} is catalog-only so {@link ProjectChangeType.VersionAdd}
 * factories use their own {@link Baseline.products}, not the parent's.
 *
 * {@link ProjectChangeType.ProductElementProductId} only uses {@link remappedIds}
 * (filled from trees/factories/EPD payloads). The current baseline element is not
 * a fallback — it may already be a different product than the change's `productId`.
 */
const remapDeletedProductIdsInChange = (
  change: ProjectChange,
  liveProductIds: ReadonlySet<ProductID>,
  catalogLiveIds: ReadonlySet<ProductID>,
  remappedIds: RemappedProductIdMap,
): boolean => {
  switch (change.type) {
    case ProjectChangeType.ElementRecipeApply: {
      let changed = remapDeletedProductIdsInTree(
        change.recipe,
        liveProductIds,
        remappedIds,
      );
      if (change.resolvedElement) {
        changed =
          remapDeletedProductIdsInTree(
            change.resolvedElement,
            liveProductIds,
            remappedIds,
          ) || changed;
      }
      return changed;
    }
    case ProjectChangeType.ProductElementEpd: {
      const remapped = getLiveFallbackProductId(
        change.productId,
        change.genericProductId,
        liveProductIds,
      );
      if (!remapped) return false;
      remappedIds.set(change.productId, remapped);
      change.productId = remapped;
      change.genericProductId = undefined;
      return true;
    }
    case ProjectChangeType.ProductElementProductId: {
      const remapped = getLiveFallbackProductId(
        change.productId,
        remappedIds.get(change.productId),
        liveProductIds,
      );
      if (!remapped) return false;
      change.productId = remapped;
      return true;
    }
    case ProjectChangeType.ElementAdd:
      return remapDeletedProductIdsInFactory(
        change.factory,
        liveProductIds,
        remappedIds,
      );
    case ProjectChangeType.VersionAdd:
      return change.factory
        ? remapDeletedProductIdsInFactory(
            change.factory,
            catalogLiveIds,
            remappedIds,
          )
        : false;
    case ProjectChangeType.ElementCategoryEnrich: {
      let changed = false;
      for (const child of change.addChildren) {
        if (
          remapDeletedProductIdsInFactory(child, liveProductIds, remappedIds)
        ) {
          changed = true;
        }
      }
      return changed;
    }
    case ProjectChangeType.ElementPropertyResolved:
    case ProjectChangeType.ElementPropertyExpression:
    case ProjectChangeType.ElementPropertyFormatted:
    case ProjectChangeType.ElementPropertyValue:
    case ProjectChangeType.ElementQuantityResolved:
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
    case ProjectChangeType.ElementQuantityFormatted:
    case ProjectChangeType.ElementQuantityValue:
    case ProjectChangeType.ElementQuantityCount:
    case ProjectChangeType.ElementQuantityFallbackCount:
    case ProjectChangeType.ElementPropertyFallbackCount:
    case ProjectChangeType.ElementPropertyCount:
    case ProjectChangeType.ElementPropertyRename:
    case ProjectChangeType.ElementDataTablePropertiesApply:
    case ProjectChangeType.ElementQuantityAutoUpdate:
    case ProjectChangeType.ElementQuantitySelect:
    case ProjectChangeType.ElementDeactivate:
    case ProjectChangeType.ElementActivate:
    case ProjectChangeType.ElementSoftDelete:
    case ProjectChangeType.ElementRecipeDetach:
    case ProjectChangeType.ElementRecipeClear:
    case ProjectChangeType.ElementCategory:
    case ProjectChangeType.ElementPropertyAdd:
    case ProjectChangeType.ElementPropertyRemove:
    case ProjectChangeType.ElementName:
    case ProjectChangeType.ElementOrigin:
    case ProjectChangeType.ElementRemove:
    case ProjectChangeType.ProductElementCountResolved:
    case ProjectChangeType.ProductElementCountExpression:
    case ProjectChangeType.ProductElementCountFormatted:
    case ProjectChangeType.ProductElementTransportOverride:
    case ProjectChangeType.ProductElementWasteOverride:
    case ProjectChangeType.ProductElementCarbonLevelOverride:
    case ProjectChangeType.ProposalResultsRecord:
    case ProjectChangeType.VersionRemove:
    case ProjectChangeType.VersionProducts:
    case ProjectChangeType.VersionResults:
    case ProjectChangeType.VersionName:
    case ProjectChangeType.VersionImports:
    case ProjectChangeType.VersionClearProposals:
    case ProjectChangeType.ProposalAdd:
    case ProjectChangeType.ProposalDuplicate:
    case ProjectChangeType.ProposalDelete:
    case ProjectChangeType.ProposalUpdate:
    case ProjectChangeType.ProposalAppendAction:
    case ProjectChangeType.ProposalReorder:
    case ProjectChangeType.ActionRename:
    case ProjectChangeType.ActionDelete:
      return false;
    default:
      return assertUnreachable(change);
  }
};

/**
 * Remap deleted `product_id` refs on one {@link Baseline}.
 * Ids present on {@link Baseline.products} are treated as live even when
 * they are gone from the catalog.
 */
const remapDeletedProductIdsInVersion = (
  version: Baseline,
  catalogLiveIds: ReadonlySet<ProductID>,
): boolean => {
  const liveProductIds = liveProductIdsWithVersionSnapshot(
    catalogLiveIds,
    version,
  );
  const remappedIds: RemappedProductIdMap = new Map();
  let changed = remapDeletedProductIdsInTree(
    version,
    liveProductIds,
    remappedIds,
  );
  const remapChange = (
    change: ProjectChange,
    includeProductIdSwitch: boolean,
  ): boolean => {
    const isProductIdSwitch =
      change.type === ProjectChangeType.ProductElementProductId;
    if (isProductIdSwitch !== includeProductIdSwitch) return false;
    return remapDeletedProductIdsInChange(
      change,
      liveProductIds,
      catalogLiveIds,
      remappedIds,
    );
  };
  for (const action of version.actions ?? []) {
    for (const change of action.changes) {
      if (remapChange(change, false)) changed = true;
    }
  }
  for (const action of version.actions ?? []) {
    for (const change of action.changes) {
      if (remapChange(change, true)) changed = true;
    }
  }
  return changed;
};

/**
 * Remap deleted `product_id` refs in a {@link Project}: baseline trees and action payloads.
 * Mutates {@link project}. Returns whether anything changed.
 *
 * Ids that still exist on {@link Baseline.products} are not remapped, even
 * if those products are deleted in the catalog. Recipes have no such snapshot and
 * still fall back to {@link IProductElement.generic_product_id}.
 *
 * {@link ProjectChangeType.ProductElementProductId} runs in a second pass so it can
 * use generics recorded from factories that appear later in the same action list.
 * It does not guess from the current baseline element's product ids.
 */
export const remapDeletedProductIdsInProject = (
  project: Project,
  liveProductIds: ReadonlySet<ProductID>,
): boolean => {
  let changed = false;
  for (const version of getAllBuildingVersions(project)) {
    if (remapDeletedProductIdsInVersion(version, liveProductIds)) {
      changed = true;
    }
  }
  return changed;
};

/**
 * Why {@link remapDeletedProductIdInPlace} rewrites this element.
 * Recipes have no version snapshot; projects remap only when the id is also absent from
 * {@link Baseline.products}.
 */
export type RemapDeletedProductPreviewReason =
  | 'catalog_product_deleted'
  | 'catalog_product_deleted_missing_from_version_snapshot';

const PREVIEW_REASON_LABEL: Record<RemapDeletedProductPreviewReason, string> = {
  catalog_product_deleted: 'Product is deleted from the catalog',
  catalog_product_deleted_missing_from_version_snapshot:
    "Product is deleted from the catalog and missing from this version's products snapshot",
};

/** One product-element rewrite for an operator preview (`product_id` + `generic_product_id`). */
export type RemappedProductElementPreview = {
  versionLabel?: string;
  elementId: string;
  elementName: string;
  reason: RemapDeletedProductPreviewReason;
  beforeProductId: ProductID;
  afterProductId: ProductID;
  beforeGenericProductId: ProductID | undefined;
  afterGenericProductId: ProductID | undefined;
};

/** Recipe or project that would change if deleted product ids are remapped. */
export type RemappedDeletedProductIdsDocumentPreview = {
  kind: 'project' | 'recipe';
  organization: string;
  documentId: string;
  documentName: string;
  elements: RemappedProductElementPreview[];
  /** True when actions/factories remapped but no baseline product element `product_id` changed. */
  actionPayloadsOnly: boolean;
};

/**
 * Product elements whose `product_id` or `generic_product_id` differs between two trees.
 */
const collectProductElementRemaps = (
  beforeRoot: OneOfSearchableElements,
  afterRoot: OneOfSearchableElements,
  options: {
    reason: RemapDeletedProductPreviewReason;
    versionLabel?: string;
  },
): RemappedProductElementPreview[] => {
  const afterById = new Map(
    getAllProductElements(afterRoot).map((element) => [element.id, element]),
  );
  const changes: RemappedProductElementPreview[] = [];
  for (const before of getAllProductElements(beforeRoot)) {
    const after = afterById.get(before.id);
    if (!after) continue;
    if (
      after.product_id === before.product_id &&
      after.generic_product_id === before.generic_product_id
    ) {
      continue;
    }
    changes.push({
      versionLabel: options.versionLabel,
      elementId: before.id,
      elementName: before.name ?? before.id,
      reason: options.reason,
      beforeProductId: before.product_id,
      afterProductId: after.product_id,
      beforeGenericProductId: before.generic_product_id,
      afterGenericProductId: after.generic_product_id,
    });
  }
  return changes;
};

/**
 * Clone {@link recipe} and list product elements that {@link remapDeletedProductIdsInRecipe} would change.
 */
export const previewRemapDeletedProductIdsInRecipe = (
  recipe: Recipe,
  liveProductIds: ReadonlySet<ProductID>,
  organization: string,
): RemappedDeletedProductIdsDocumentPreview | undefined => {
  const clone = cloneDeep(recipe);
  if (!remapDeletedProductIdsInRecipe(clone, liveProductIds)) return undefined;
  const elements = collectProductElementRemaps(recipe, clone, {
    reason: 'catalog_product_deleted',
  });
  return {
    kind: 'recipe',
    organization,
    documentId: recipe.id,
    documentName: recipe.name,
    elements,
    actionPayloadsOnly: elements.length === 0,
  };
};

/**
 * Clone {@link project} and list baseline product elements that
 * {@link remapDeletedProductIdsInProject} would change.
 */
export const previewRemapDeletedProductIdsInProject = (
  project: Project,
  liveProductIds: ReadonlySet<ProductID>,
  organization: string,
): RemappedDeletedProductIdsDocumentPreview | undefined => {
  const clone = cloneDeep(project);
  if (!remapDeletedProductIdsInProject(clone, liveProductIds)) return undefined;
  const elements: RemappedProductElementPreview[] = [];
  const originalVersions = getAllBuildingVersions(project);
  const remappedVersions = getAllBuildingVersions(clone);
  for (let i = 0; i < originalVersions.length; i++) {
    const before = originalVersions[i];
    const after = remappedVersions[i];
    if (!before || !after) continue;
    elements.push(
      ...collectProductElementRemaps(before, after, {
        reason: 'catalog_product_deleted_missing_from_version_snapshot',
        versionLabel: before.name || before.id,
      }),
    );
  }
  return {
    kind: 'project',
    organization,
    documentId: String(project.id),
    documentName: project.name,
    elements,
    actionPayloadsOnly: elements.length === 0,
  };
};

const formatProductIdForPreview = (
  productId: ProductID | undefined,
  productNames: ReadonlyMap<ProductID, string>,
  emptyLabel: string,
): string => {
  if (!productId) return emptyLabel;
  const name = productNames.get(productId);
  return name ? `${productId} ("${name}")` : productId;
};

/**
 * Nested operator list: organization → project/recipe → version → element
 * reason, `product_id`, and `generic_product_id` before/after.
 */
export const formatRemapDeletedProductIdsPreview = (
  previews: readonly RemappedDeletedProductIdsDocumentPreview[],
  productNames: ReadonlyMap<ProductID, string> = new Map(),
): string[] => {
  if (previews.length === 0) return ['No remaps.'];

  const byOrg = new Map<string, RemappedDeletedProductIdsDocumentPreview[]>();
  for (const preview of previews) {
    const group = byOrg.get(preview.organization) ?? [];
    group.push(preview);
    byOrg.set(preview.organization, group);
  }

  const lines: string[] = [];
  const organizations = [...byOrg.keys()].sort((a, b) => a.localeCompare(b));
  for (const organization of organizations) {
    const documents = byOrg.get(organization);
    if (!documents) continue;
    documents.sort((a, b) => a.documentName.localeCompare(b.documentName));
    lines.push(`Organization: ${organization}`);
    for (const document of documents) {
      const kindLabel = document.kind === 'project' ? 'Project' : 'Recipe';
      lines.push(
        `  ${kindLabel}: ${document.documentName} (${document.documentId})`,
      );
      if (document.actionPayloadsOnly) {
        lines.push(
          '    (no baseline element product_id change; deleted ids rewritten in stored proposal actions)',
        );
        continue;
      }
      let currentVersion: string | undefined;
      for (const element of document.elements) {
        if (element.versionLabel && element.versionLabel !== currentVersion) {
          currentVersion = element.versionLabel;
          lines.push(`    Version: ${currentVersion}`);
        }
        lines.push(
          `      Element: ${element.elementName} (${element.elementId})`,
        );
        lines.push(`        reason: ${PREVIEW_REASON_LABEL[element.reason]}`);
        lines.push('        product_id:');
        lines.push(
          `          before: ${formatProductIdForPreview(element.beforeProductId, productNames, '(none)')}`,
        );
        lines.push(
          `          after:  ${formatProductIdForPreview(element.afterProductId, productNames, '(none)')}`,
        );
        lines.push('        generic_product_id:');
        lines.push(
          `          before: ${formatProductIdForPreview(element.beforeGenericProductId, productNames, '(none)')}`,
        );
        lines.push(
          `          after:  ${formatProductIdForPreview(element.afterGenericProductId, productNames, '(cleared)')}`,
        );
      }
    }
  }
  return lines;
};

/**
 * Read the scoped override for a specific target on a {@link IProductElement} (or any shape
 * that carries `productOverrides`, e.g. list-view items).
 * Returns `undefined` when no override exists on {@link ProductElementOverrides}.
 */
export const getProductOverride = <Target extends ProductElementOverrideTarget>(
  productElement: Pick<IProductElement, 'productOverrides'>,
  target: Target,
): ProductElementOverrides[Target] => {
  return productElement.productOverrides?.[target];
};
/**
 * Replace a single override value on a {@link IProductElement}.
 * Preserves existing {@link ProductElementOverrides} for other targets.
 */
export const setProductOverride = <Target extends ProductElementOverrideTarget>(
  productElement: IProductElement,
  target: Target,
  override: ProductElementOverrides[Target],
): IProductElement => {
  const newProductElement = cloneDeep(productElement);
  newProductElement.productOverrides = {
    ...productElement.productOverrides,
    [target]: override,
  };
  return newProductElement;
};

/**
 * Merge a set of overrides into the {@link ProductElementOverrides} on a
 * {@link IProductElement}. Returns a new IProductElement
 */
export const mergeProductOverrides = (
  productElement: IProductElement,
  overrides: ProductElementOverrides,
): IProductElement => ({
  ...productElement,
  productOverrides: {
    ...productElement.productOverrides,
    ...overrides,
  },
});

/**
 * Market percentile at the slider midpoint — zero CO₂/cost adjustment, same as having no override.
 * Matches {@link getCo2eAdjustmentValues} default when {@link IProductElement.productOverrides} is absent.
 */
export const MARKET_PERCENTILE_NO_ADJUSTMENT = 50;

const isNoAdjustmentMarketPercentileOverride = (
  entry: ProductElementOverrides[ProductElementOverrideTarget] | undefined,
): boolean => {
  if (!entry) return false;
  return (
    entry.override.type === 'marketPercentileMultiplier' &&
    entry.override.value === MARKET_PERCENTILE_NO_ADJUSTMENT
  );
};

/**
 * Removes override entries that only encode the default no-adjustment percentile.
 * Absent overrides and P50 {@link OverrideTypes.marketPercentileMultiplier} are pipeline-equivalent.
 */
export const normalizeProductOverridesForEquivalence = (
  overrides: ProductElementOverrides | undefined,
): ProductElementOverrides | undefined => {
  if (!overrides) return undefined;

  const normalized = { ...overrides };
  for (const target of Object.keys(
    normalized,
  ) as ProductElementOverrideTarget[]) {
    if (isNoAdjustmentMarketPercentileOverride(normalized[target])) {
      delete normalized[target];
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

/**
 * Merges a partial override patch into existing overrides.
 * `undefined` patch values remove a target (same as bulk-edit clear).
 */
export const mergeProductOverridePatch = (
  existing: ProductElementOverrides | undefined,
  patch: Partial<ProductElementOverrides>,
): ProductElementOverrides | undefined => {
  const merged: ProductElementOverrides = {
    ...(existing ?? {}),
    ...patch,
  };

  for (const target of Object.keys(patch) as ProductElementOverrideTarget[]) {
    if (patch[target] === undefined) delete merged[target];
  }

  return Object.keys(merged).length > 0 ? merged : undefined;
};

/**
 * Whether two override maps yield the same conversion-factor adjustments.
 */
export const areProductOverridesEffectivelyEqual = (
  left: ProductElementOverrides | undefined,
  right: ProductElementOverrides | undefined,
): boolean =>
  isEqual(
    normalizeProductOverridesForEquivalence(left),
    normalizeProductOverridesForEquivalence(right),
  );

/**
 * Remove one or more overrides from {@link ProductElementOverrides} on a
 * {@link IProductElement}. No-op if targets are missing.
 */
export const removeProductOverride = (
  productElement: IProductElement,
  ...targets: ProductElementOverrideTarget[]
): IProductElement => {
  if (targets.length === 0) return productElement;
  const newProductElement = cloneDeep(productElement);
  targets.forEach((target) => {
    delete newProductElement.productOverrides?.[target];
  });
  return newProductElement;
};

/**
 * Remove EPD on multiple product elements.
 * @param productElements
 * @returns
 */
export const clearEpdOnProductElements = (productElements: IProductElement[]) =>
  shallowEqualMap(productElements, clearProductElementEpd);

/**
 * Set the EPD on a product element.
 * @param productElement
 * @param product_id
 * @param generic_id Provide a new generic id if you want to override the existing one, else we'll keep the existing one
 * @returns
 */
export const setProductElementEpd = (
  productElement: IProductElement,
  product_id: ProductID,
  generic_id?: ProductID,
): IProductElement => {
  // Only set if the product element does not already have the EPD
  if (getEPDProductId(productElement) !== product_id) {
    const generic_product_id =
      generic_id ?? getGenericProductId(productElement);
    return { ...productElement, product_id, generic_product_id };
  }
  return productElement;
};

/**
 * Set EPD on multiple product elements.
 * @param productElements
 * @param product_id
 * @param generic_id Provide a new generic id if you want to override the existing one, else we'll keep the existing one
 * @returns
 */
export const setEpdOnProductElements = (
  productElements: IProductElement[],
  product_id: ProductID,
  generic_id?: ProductID,
) =>
  shallowEqualMap(productElements, (el) =>
    setProductElementEpd(el, product_id, generic_id),
  );

/**
 * Get generic product id from a product element.
 * Should always exist
 * @param product
 * @returns
 */
export const getGenericProductId = (
  productOrId: IProductReference | ProductID,
): ProductID => {
  if (typeof productOrId === 'string') {
    return validateGenericProductId(productOrId);
  }
  // Product_id is the generic id if generic_product id is not specified
  return validateGenericProductId(
    productOrId.generic_product_id ?? productOrId.product_id,
  );
};

/**
 * Get EPD product id from a product element.
 * If no generic product id is specified it will return undefined.
 * @param product
 * @returns
 */
export const getEPDProductId = (
  productOrId: IProductReference | ProductID,
): ProductID | undefined => {
  if (typeof productOrId === 'string') {
    return productOrId;
  }
  return hasProductElementEpd(productOrId) ? productOrId.product_id : undefined;
};

/**
 * Whether a product element uses **generic** (schablon/Boverket) climate data rather than a
 * **specific** EPD. Generic A1–A3 is the only carbon that may receive the conservative +25%
 * uplift (see {@link getEPDProductId} and the Boverket GK/SK split); EPD-backed elements are
 * "specific" and must never be uplifted. This mirrors the A1–A3 branch of
 * `splitProductClimateResultsGkSk` and is the single reusable predicate for that decision.
 * @param element - Product element or reference to classify.
 * @returns `true` when the element resolves to generic data (no EPD selected).
 */
export const isGenericProductElement = (
  element: IProductReference | ProductID,
): boolean => !getEPDProductId(element);

/**
 * Whether a product element carries a user-set carbon level (A1–A3) override from the carbon
 * slider ({@link ProductElementOverrideTarget.CO2E_A1_A3}). Such a value is treated as
 * **specific** (user-chosen), so the conservative generic uplift must not be applied on top of
 * it (DEV-3411).
 * @param element - Product element to inspect.
 */
export const hasCarbonLevelOverride = (element: IProductElement): boolean =>
  guards.is.defined(
    getProductOverride(element, ProductElementOverrideTarget.CO2E_A1_A3),
  );

/**
 * Structural shape needed to classify a product element's climate-data source. Matches both
 * {@link IProductElement} and list-view variants ({@link IProductListItem}, {@link
 * IProductListGroup}) that redeclare `kind` with a different discriminant.
 */
export type ProductElementLike = IProductReference &
  Pick<IProductElement, 'productOverrides' | 'unit'>;

/**
 * Climate-data badge payload for element/product list rows (DEV-3479). Only EPD-linked products
 * get a tag; carbon-level overrides alone do not.
 */
export type ProductElementClimateTag = {
  /**
   * CO₂ adjustment vs generic baseline as percent (e.g. `-37` for `-37%`). Undefined when the
   * generic baseline cannot be resolved (e.g. an EPD-only custom product) — the badge is still
   * shown, only the percent is omitted from the tooltip.
   */
  percentAdjustment?: number;
  /** Resolved EPD product name for tooltips. */
  epdName?: string;
};

/** Badge label rendered next to an EPD-linked product name. */
export const PRODUCT_ELEMENT_CLIMATE_TAG_LABEL = 'EPD';

/**
 * Signed percent string for climate-tag tooltips (e.g. `-37%`, `+5%`).
 */
const formatClimateTagPercent = (percentAdjustment: number): string =>
  `${percentAdjustment > 0 ? '+' : ''}${percentAdjustment}%`;

/**
 * EPD badge tooltip: product name with carbon efficiency in parentheses (DEV-3479).
 * Example: `EPD gypsum (-37%)`. Falls back to the plain badge label when neither the EPD name
 * nor the percent can be resolved.
 */
export const getProductElementClimateTagEpdTooltip = (
  tag: ProductElementClimateTag,
): string => {
  const percentLabel = guards.is.number(tag.percentAdjustment)
    ? `(${formatClimateTagPercent(tag.percentAdjustment)})`
    : undefined;
  return (
    [tag.epdName, percentLabel].filter(guards.is.defined).join(' ') ||
    PRODUCT_ELEMENT_CLIMATE_TAG_LABEL
  );
};

/**
 * Resolve a product from the version/store map, falling back to built-in generics.
 * A stored entry wins, including when its conversion factor is 0.
 * @param productsMap - Version and store products. Missing ids fall through to the catalog.
 * @param productId - Generic or EPD id to resolve
 */
export const resolveProductFromMap = (
  productsMap: ProductRecord | undefined,
  productId: ProductID,
): IProduct | undefined =>
  productsMap?.[productId] ?? genericProductsLookup[productId];

/**
 * Percent CO₂ adjustment of an EPD relative to its generic baseline — same formula as
 * {@link getEPDPropertyDelta} in product bulk edit, using {@link getCombinedConversionFactors}.
 */
const getEpdCo2PercentAdjustment = (
  element: ProductElementLike,
  productsMap?: ProductRecord,
): number | undefined => {
  const epdId = getEPDProductId(element);
  if (!epdId) return undefined;

  const genericId = getGenericProductId(element);
  const genericProduct = resolveProductFromMap(productsMap, genericId);
  if (!genericProduct) return undefined;

  const epdProduct = resolveProductFromMap(productsMap, epdId);
  if (!epdProduct) return undefined;

  const unit = element.unit ?? genericProduct.unit;
  if (!unit) return undefined;

  const genericCo2 = getConversionFactorValue(
    getCombinedConversionFactors(undefined, genericProduct, unit),
    'co2e_A1-A3',
  );
  const epdCo2 = getConversionFactorValue(
    getCombinedConversionFactors(epdProduct, genericProduct, unit),
    'co2e_A1-A3',
  );
  if (
    !guards.is.number(genericCo2) ||
    genericCo2 === 0 ||
    !guards.is.number(epdCo2)
  ) {
    return undefined;
  }

  return Math.round(((epdCo2 - genericCo2) / genericCo2) * 100);
};

/**
 * Derive the {@link ProductElementClimateTag} for a product element, or `undefined` when there
 * is no linked EPD. Carbon-level overrides alone do not produce a list tag (DEV-3479 UX).
 * An EPD whose generic baseline cannot be resolved still gets a tag, just without a percent.
 * @param element - Product element (or list-view equivalent) to inspect.
 * @param productsMap - Version products for EPD-vs-generic percent scoring; optional fallback
 *   when omitted.
 */
export const getProductElementClimateTag = (
  element: ProductElementLike,
  productsMap?: ProductRecord,
): ProductElementClimateTag | undefined => {
  const epdId = getEPDProductId(element);
  if (!epdId) return undefined;

  return {
    percentAdjustment: getEpdCo2PercentAdjustment(element, productsMap),
    epdName: resolveProductFromMap(productsMap, epdId)?.name,
  };
};

/**
 * Reduce a list of product elements to a single {@link ProductElementClimateTag}, but only
 * when every element resolves to the *same* EPD tag. Returns `undefined` for an empty list or a
 * mixed set (e.g. one EPD-linked element alongside a generic one) to avoid a misleading badge
 * on a summary/group row (DEV-3479).
 */
export const getHomogeneousClimateTag = (
  elements: ProductElementLike[],
  productsMap?: ProductRecord,
): ProductElementClimateTag | undefined => {
  const [first, ...rest] = elements;
  if (!first) return undefined;

  const tag = getProductElementClimateTag(first, productsMap);
  if (!tag) return undefined;

  const isHomogeneous = rest.every((element) =>
    isEqual(getProductElementClimateTag(element, productsMap), tag),
  );
  return isHomogeneous ? tag : undefined;
};

/**
 * Auto-generated reinforcement steel product embedded alongside cast concrete (see
 * `concrete.ts` `getChildElements`). Excluded from {@link getElementClimateTag} so a concrete
 * slot's badge always reflects the concrete itself, never the rebar (DEV-3479).
 */
const REINFORCEMENT_STEEL_PRODUCT_IDS = [
  ConcreteProducts.ReinforcementSteel,
  ConcreteProducts.ReinforcementSteelStainless,
] as const;

const isReinforcementSteelProductElement = (
  element: ProductElementLike,
): boolean =>
  isOneOf(
    REINFORCEMENT_STEEL_PRODUCT_IDS,
    element.generic_product_id ?? element.product_id,
  );

/**
 * Derive the {@link ProductElementClimateTag} for a structural element row in the Elements
 * tab (e.g. an Insulation or Concrete category slot), from its direct product children.
 * Reinforcement steel (auto-generated alongside concrete) is ignored so the tag always
 * reflects the main product. Container elements without product children (most structural
 * elements) simply return `undefined` — only elements that wrap one or more products in the
 * same climate-data state get a badge (DEV-3479).
 */
export const getElementClimateTag = (
  element: IElement,
  productsMap?: ProductRecord,
): ProductElementClimateTag | undefined => {
  const productChildren = getChildElements(element)
    .filter(isProductElement)
    .filter((child) => !isReinforcementSteelProductElement(child));
  return getHomogeneousClimateTag(productChildren, productsMap);
};

export const getProductElementData = (
  element: IProductElement,
):
  | { type: 'generic'; genericId: ProductID; currentId: ProductID }
  | {
      type: 'epd';
      epdId: ProductID;
      genericId: ProductID;
      currentId: ProductID;
    } => {
  const genericId = getGenericProductId(element);
  const epdId = getEPDProductId(element);

  return guards.is.defined(epdId)
    ? { type: 'epd', epdId, genericId, currentId: epdId }
    : { type: 'generic', genericId, currentId: genericId };
};

/**
 * Get the product description from corresponding product element
 * @param element
 * @param version
 * @returns
 */
export const getProductDescription = (
  element: OneOfElements,
  version?: Baseline,
): string => {
  if (isProductElement(element)) {
    const epdId = getEPDProductId(element);

    if (epdId) {
      const epd = getEPDProduct(version?.products, epdId);
      return epd?.description ?? '';
    }
  }
  return '';
};

/**
 * Get conversion factors for a product element in the product element unit.
 * For generic-only elements, all overrides are applied to the generic product.
 * For EPD elements, generic-only overrides are applied to the generic product,
 * then combined with the EPD, then cascading overrides are applied to the result.
 * @param productRecordOwner - Version, path, or product record to look up products from
 * @param element - The product element
 * @param product - If you have an EPD that is not yet added to the version, pass it in here
 * @param marketEstimates - Project-level market estimates for percentile-based overrides
 * @returns Conversion factors for the product element
 */
export const getProductElementConversionFactors = (
  productRecordOwner: ProductRecordOwners,
  element: IProductElement,
  product?: IProduct,
  marketEstimates?: ProjectMarketEstimates,
): Results => {
  const data = getProductElementData(element);
  const genericProduct = getProductById(productRecordOwner, data.genericId);
  const productEstimate = getProductEstimates(marketEstimates, data.genericId);
  const overrides = element.productOverrides;

  // Generic: epdProduct undefined → convert to element.unit only. EPD: combine then convert.
  const epdProduct =
    data.type === 'epd'
      ? (getProductById(productRecordOwner, data.epdId) ?? product)
      : undefined;

  return applyProductElementOverrides(
    genericProduct,
    overrides,
    productEstimate,
    { epdProduct, unit: element.unit },
  ).conversion_factors;
};

/**
 * Check if a product element unit is supported. (must exist in the conversion factors)
 * @param productRecordOwner
 * @param element
 * @returns
 */
export const isSupportedProductElementUnit = (
  productRecordOwner: ProductRecordOwners,
  element: IProductElement,
): boolean => {
  const conversionFactors = getProductElementConversionFactors(
    productRecordOwner,
    element,
  );
  return typeof conversionFactors[element.unit] === 'number';
};

/**
 * Get multiplier from an override. Only {@link ProductElementOverride} marketPercentileMultiplier
 * yields a non-1 multiplier (percentile → factor via {@link percentileToFactor}); others return 1.
 *
 * IMPORTANT: When {@link PercentileFactorRange} is undefined (no market estimates), this returns 1
 * and no adjustment is applied.
 */
const getOverrideMultiplier = (
  override: ProductElementOverride,
  range: PercentileFactorRange | undefined,
): number => {
  if (!range) return 1;
  switch (override.type) {
    case 'marketPercentileMultiplier':
      return 1 + percentileToFactor(override.value, range);
    default:
      return 1;
  }
};

type ApplyOverrideContext = {
  factors: ConversionFactors;
  estimate?: ProductMarketEstimate;
};

/**
 * Override applicator definition with explicit dependency metadata.
 * The {@link dependsOn} array declares which conversion factors this applicator
 * reads from other applicators' outputs. {@link topoSortTargets} uses this to
 * guarantee that dependencies are resolved before dependents.
 */
export type OverrideApplicatorDef = {
  produces: ConversionFactorUnit;
  dependsOn: readonly ConversionFactorUnit[];
  apply: (
    override: ProductElementOverride,
    ctx: ApplyOverrideContext,
  ) => ConversionFactors;
  /** Auto-derive from baseline when no explicit override exists */
  derive?: (
    factors: ConversionFactors,
    baseline: ConversionFactors,
  ) => ConversionFactors;
};

/**
 * Topological sort of override targets by their dependency graph.
 * Throws if the graph contains a cycle.
 */
export const topoSortTargets = <T extends string>(
  defs: Record<
    T,
    {
      produces: ConversionFactorUnit;
      dependsOn: readonly ConversionFactorUnit[];
    }
  >,
): T[] => {
  const targets = Object.keys(defs) as T[];

  const producerOf = new Map<ConversionFactorUnit, T>();
  for (const t of targets) {
    producerOf.set(defs[t].produces, t);
  }

  // prerequisites: target → set of targets that must run before it
  const prerequisites = new Map<T, Set<T>>(
    targets.map((t) => {
      const prereqs = new Set<T>();
      for (const dep of defs[t].dependsOn) {
        const producer = producerOf.get(dep);
        if (producer) prereqs.add(producer);
      }
      return [t, prereqs];
    }),
  );

  const sorted: T[] = [];
  const remaining = new Map(
    [...prerequisites].map(([k, v]) => [k, new Set(v)] as const),
  );

  while (remaining.size > 0) {
    const ready = [...remaining.entries()]
      .filter(([, deps]) => deps.size === 0)
      .map(([target]) => target);

    if (ready.length === 0) {
      throw new Error(
        `Circular dependency in override applicators: ${[...remaining.keys()].join(', ')}`,
      );
    }

    for (const target of ready) {
      sorted.push(target);
      remaining.delete(target);
      for (const deps of remaining.values()) deps.delete(target);
    }
  }

  return sorted;
};

/**
 * Override applicator definitions keyed by {@link ProductElementOverrideTarget}.
 * Each entry declares its produced factor, dependencies, an {@link apply} function
 * for explicit overrides, and an optional {@link derive} function for auto-deriving
 * from the baseline when no explicit override exists.
 *
 * The {@link Record} type guarantees every target has a definition at compile time.
 */
export const overrideApplicators: Record<
  ProductElementOverrideTarget,
  OverrideApplicatorDef
> = {
  [ProductElementOverrideTarget.CO2E_A1_A3]: {
    produces: 'co2e_A1-A3',
    dependsOn: [],
    apply: (override, { factors, estimate }) => {
      const current = factors['co2e_A1-A3'];
      if (!guards.is.number(current)) return factors;
      const multiplier = getOverrideMultiplier(override, estimate?.carbon);
      return { ...factors, 'co2e_A1-A3': current * multiplier };
    },
  },
  [ProductElementOverrideTarget.SEK_A1_A3]: {
    produces: 'sek_A1-A3',
    dependsOn: [],
    apply: (override, { factors, estimate }) => {
      const current = factors['sek_A1-A3'];
      if (!guards.is.number(current)) return factors;
      const multiplier = getOverrideMultiplier(override, estimate?.cost);
      return { ...factors, 'sek_A1-A3': current * multiplier };
    },
  },
  [ProductElementOverrideTarget.TRANSPORT_KM]: {
    produces: 'co2e_A4',
    dependsOn: ['kg'],
    apply: (override, { factors }) => ({
      ...factors,
      co2e_A4: kmToA4(override.value, factors.kg ?? 0),
    }),
  },
  [ProductElementOverrideTarget.WASTE_PERCENT]: {
    produces: 'co2e_A5',
    dependsOn: ['co2e_A1-A3', 'co2e_A4'],
    apply: (override, { factors }) => ({
      ...factors,
      co2e_A5: getA5FromWasteFactorPercentage(factors, override.value),
    }),
    derive: (factors, baseline) => {
      if (!baseline.co2e_A5) return factors;
      const wastePercent = getProductWasteFactor(baseline) * 100;
      return {
        ...factors,
        co2e_A5: getA5FromWasteFactorPercentage(factors, wastePercent),
      };
    },
  },
};

/** Targets in dependency-resolved order, computed once at module load. */
const sortedTargets = topoSortTargets(overrideApplicators);

/**
 * Apply all product element overrides to a product's conversion factors.
 *
 * Override applicators are processed in dependency-sorted order
 * (see {@link topoSortTargets}), guaranteeing that if factor B depends on
 * factor A, A is resolved before B. After all explicit overrides, derived
 * values (e.g. A5 from waste factor) are auto-computed for targets that
 * have a {@link OverrideApplicatorDef.derive derive} function and no
 * explicit override.
 *
 * Scoped application order:
 *  1. `'genericProduct'` scoped overrides → applied to the generic product
 *  2. If EPD: combine EPD + modified generic
 *  3. `'all'` scoped overrides → applied to the (combined) result
 *  4. Derived values for targets without explicit overrides
 *
 * @param product - The generic product to apply overrides to
 * @param overrides - The {@link ProductElementOverrides} to apply
 * @param estimate - Market estimates for percentile-based overrides
 * @param epd - Optional EPD context; when present, generic-product overrides apply before combining
 * @returns Product with overridden conversion factors
 */
export const applyProductElementOverrides = (
  product: IProduct,
  overrides?: ProductElementOverrides,
  estimate?: ProductMarketEstimate,
  epd?: { epdProduct: IProduct | undefined; unit: ConversionFactorUnit },
): IProduct => {
  if (guards.is.undefined(overrides)) {
    if (!epd) return product;
    const combined = getCombinedConversionFactors(
      epd.epdProduct,
      product,
      epd.unit,
    );
    return { ...product, conversion_factors: combined };
  }

  const applyByScope = (
    factors: ConversionFactors,
    scope: ProductElementOverrideScope,
  ): ConversionFactors => {
    const ctx: ApplyOverrideContext = { factors, estimate };
    for (const target of sortedTargets) {
      const entry = overrides[target];
      if (!entry || entry.applicationScope !== scope) continue;
      ctx.factors = overrideApplicators[target].apply(entry.override, ctx);
    }
    return ctx.factors;
  };

  const baseline = epd
    ? getCombinedConversionFactors(epd.epdProduct, product, epd.unit)
    : product.conversion_factors;

  // 1. Apply generic-product scoped overrides to the generic product
  let factors = applyByScope(
    { ...product.conversion_factors },
    'genericProduct',
  );

  // 2. If EPD, combine EPD with modified generic product
  if (epd) {
    factors = getCombinedConversionFactors(
      epd.epdProduct,
      { ...product, conversion_factors: factors },
      epd.unit,
    );
  }

  // 3. Apply cascading overrides (dependency-sorted)
  factors = applyByScope(factors, 'all');

  // 4. Auto-derive values for targets without explicit overrides
  for (const target of sortedTargets) {
    const def = overrideApplicators[target];
    if (overrides[target] || !def.derive) continue;
    factors = def.derive(factors, baseline);
  }

  return { ...product, conversion_factors: factors };
};

/**
 * Check if a product element has a marketPercentileMultiplier override.
 */
export const hasPercentileOverride = (element: IProductElement): boolean => {
  const overrides = element.productOverrides;
  if (!overrides) return false;
  const co2eEntry = overrides[ProductElementOverrideTarget.CO2E_A1_A3];
  const costEntry = overrides[ProductElementOverrideTarget.SEK_A1_A3];
  return (
    co2eEntry?.override.type === 'marketPercentileMultiplier' ||
    costEntry?.override.type === 'marketPercentileMultiplier'
  );
};
